// The step bar over a multi-step form (the campaign editor, UI-2). A table of
// contents, not a gate: every step that exists can be opened, in any order,
// because a marketer goes back to the audience after seeing the estimate and a
// form that forbids that is one people work around. What a step still lacks is
// said under its name, and the review step is where it becomes a condition.

import { CheckIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export type StepStatus = "done" | "todo" | "attention";

export interface StepItem<K extends string = string> {
  key: K;
  label: string;
  status: StepStatus;
  /** One short line under the label: "3 ország", "jóváhagyás hiányzik". */
  hint?: string;
  /** Not openable yet — before the campaign exists, only the first step is. */
  disabled?: boolean;
}

interface Props<K extends string> {
  steps: StepItem<K>[];
  current: K;
  onSelect: (key: K) => void;
  className?: string;
}

const MARK: Record<StepStatus, string> = {
  done: "border-primary bg-primary text-on-accent",
  todo: "border-line-strong bg-surface text-muted-foreground",
  attention: "border-warn bg-warn-soft text-warn",
};

export function Stepper<K extends string>({ steps, current, onSelect, className }: Props<K>) {
  return (
    <nav aria-label="Lépések" className={cn("overflow-x-auto", className)} data-slot="stepper">
      <ol className="m-0 flex min-w-max list-none items-stretch gap-0 p-0">
        {steps.map((step, index) => {
          const active = step.key === current;
          return (
            <li key={step.key} className="flex items-center">
              {index > 0 && <span aria-hidden="true" className="mx-2 h-px w-6 bg-line-strong lg:w-10" />}
              <button
                type="button"
                disabled={step.disabled}
                aria-current={active ? "step" : undefined}
                onClick={() => onSelect(step.key)}
                className={cn(
                  "flex cursor-pointer items-center gap-2.5 rounded-control px-2 py-1.5 text-left hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-55 disabled:hover:bg-transparent",
                  active && "bg-primary-soft hover:bg-primary-soft",
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    "grid size-6 shrink-0 place-items-center rounded-pill border text-label font-semibold",
                    MARK[step.status],
                    active && step.status === "todo" && "border-primary text-blue-ink",
                  )}
                >
                  {step.status === "done" ? <CheckIcon className="size-3.5" /> : step.status === "attention" ? "!" : index + 1}
                </span>
                <span className="flex flex-col">
                  <span className={cn("font-medium", active ? "text-blue-ink" : "text-ink")}>
                    <span className="sr-only">{index + 1}. lépés: </span>
                    {step.label}
                  </span>
                  {step.hint && <span className="text-label text-muted-foreground">{step.hint}</span>}
                </span>
                <span className="sr-only">
                  {step.status === "done" ? " — kész" : step.status === "attention" ? " — figyelmet kér" : ""}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
